import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  PlusIcon,
  WrenchScrewdriverIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  DocumentChartBarIcon,
  QuestionMarkCircleIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
  RectangleGroupIcon,
  BuildingOffice2Icon,
  ClipboardDocumentListIcon,
  BugAntIcon,
} from '@heroicons/react/24/outline';
import { authService } from '../services/authService';
import { useUser } from '../contexts/UserContext';
import { getEnvironmentTitle } from '../utils/environmentUtils';
import { apiService } from '../services/apiService';

const Layout = ({ children }) => {
  const location = useLocation();
  const userProfile = authService.getUserProfile();
  const { isAdminOrTechnician, isAdmin, userRole } = useUser();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [projectTrackerEnabled, setProjectTrackerEnabled] = useState(false);

  useEffect(() => {
    if (userRole !== 'Admin') {
      setProjectTrackerEnabled(false);
      return;
    }
    apiService.isProjectTrackerEnabled().then(setProjectTrackerEnabled).catch(() => {});
  }, [userRole]);

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    { name: 'New Ticket', href: '/new-ticket', icon: PlusIcon },
    { name: 'Log Exergy Issue', href: '/log-issue', icon: BugAntIcon },
    ...(isAdminOrTechnician() ? [
      { name: 'Technician View', href: '/technician', icon: WrenchScrewdriverIcon },
      { name: 'Reports', href: '/reports', icon: DocumentChartBarIcon },
      { name: 'Admin Centers', href: '/admin-centers', icon: BuildingOffice2Icon },
      ...(projectTrackerEnabled ? [
        { name: 'Projects', href: '/projects', icon: ClipboardDocumentListIcon },
      ] : []),
    ] : []),
    ...(isAdmin() ? [
      { name: 'Security Center', href: '/control-center', icon: RectangleGroupIcon },
      { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
    ] : []),
    { name: 'Help', href: '/help', icon: QuestionMarkCircleIcon },
    { name: 'Alula finops', href: 'https://finops.alulatechnologies.com/', icon: ArrowTopRightOnSquareIcon, external: true },
  ];

  const handleLogout = async () => {
    try {
      await authService.logout();
      // Redirect to login page instead of reloading
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, clear state and redirect
      authService.clearAuthState();
      window.location.href = '/';
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75"></div>
        </div>
      )}

      {/* Sidebar */}
      <div className={`${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } md:translate-x-0 fixed md:static inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out md:flex md:flex-shrink-0`}>
        <div className="flex flex-col w-64 h-full">
          <div className="flex flex-col h-full bg-gray-800">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div className="flex items-center justify-between flex-shrink-0 px-4">
                <h1 className="text-white text-xl font-bold">
                  {getEnvironmentTitle()}
                </h1>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="md:hidden text-gray-400 hover:text-white"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <nav className="mt-5 flex-1 px-2 bg-gray-800 space-y-1">
                {navigation.map((item) => {
                  const isActive = !item.external && location.pathname === item.href;
                  const linkClass = `${
                    isActive
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  } group flex items-center px-2 py-2 text-sm font-medium rounded-md`;
                  const iconClass = `${
                    isActive ? 'text-gray-300' : 'text-gray-400 group-hover:text-gray-300'
                  } mr-3 flex-shrink-0 h-6 w-6`;
                  if (item.external) {
                    return (
                      <a
                        key={item.name}
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setSidebarOpen(false)}
                        className={linkClass}
                      >
                        <item.icon className={iconClass} aria-hidden="true" />
                        {item.name}
                      </a>
                    );
                  }
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={linkClass}
                    >
                      <item.icon className={iconClass} aria-hidden="true" />
                      {item.name}
                    </Link>
                  );
                })}
              </nav>
            </div>
            <div className="flex-shrink-0 flex bg-gray-700 p-4">
              <div className="flex items-center w-full">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {userProfile?.displayName || userProfile?.name || 'User'}
                  </p>
                </div>
                <button
                  onClick={handleLogout}
                  className="ml-3 flex-shrink-0 bg-gray-600 p-1 rounded-md text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-700 focus:ring-white"
                  title="Logout"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Mobile header with hamburger menu */}
        <div className="md:hidden bg-white shadow-sm border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
            <h1 className="text-lg font-semibold text-gray-900">
              {getEnvironmentTitle()}
            </h1>
            <div className="w-6"></div> {/* Spacer for centering */}
          </div>
        </div>
        
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout; 